import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Send, ShieldAlert, Copy, Paperclip, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * V160.3 — Admin Messages tab inside AssignmentDetailModal.
 *
 * Shows the parent ↔ tutor thread (with the same server-side redaction
 * both parties see), a PII flag per row, live presence dots, and one-tap
 * WhatsApp nudge buttons for both parties. Admin can inject a system
 * message into the thread — same redaction rules apply.
 */

const PRESENCE_POLL_MS = 10000;
const THREAD_POLL_MS = 8000;

export default function AdminMessagesPanel({ assignment }) {
    const [thread, setThread] = useState(null);
    const [presence, setPresence] = useState({ parent: null, tutor: null });
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const listRef = useRef(null);

    const reloadThread = useCallback(async () => {
        try {
            const r = await api.get(`/admin/assignments/${assignment.id}/messages?_=${Date.now()}`);
            setThread(r.data);
        } catch (_e) { /* transient */ }
        finally { setLoading(false); }
    }, [assignment.id]);

    const reloadPresence = useCallback(async () => {
        try {
            const r = await api.get(`/admin/assignments/${assignment.id}/messages/presence?_=${Date.now()}`);
            setPresence(r.data || { parent: null, tutor: null });
        } catch (_e) { /* transient */ }
    }, [assignment.id]);

    useEffect(() => {
        reloadThread();
        reloadPresence();
        const t = setInterval(reloadThread, THREAD_POLL_MS);
        const p = setInterval(reloadPresence, PRESENCE_POLL_MS);
        return () => { clearInterval(t); clearInterval(p); };
    }, [reloadThread, reloadPresence]);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [thread?.items?.length]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        try {
            await api.post(`/admin/assignments/${assignment.id}/messages`, { body });
            setDraft("");
            reloadThread();
            toast.success("System note added to thread");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const items = thread?.items || [];
    const piiCount = thread?.pii_flag_count || 0;

    return (
        <div className="space-y-4" data-testid="assignment-messages-panel">
            {/* Header + presence + WA nudge buttons */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="flex items-center gap-3 min-w-0">
                    <MessageSquare className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A]">Portal chat</div>
                        <div className="text-[11px] text-slate-500">
                            {items.length} message{items.length === 1 ? "" : "s"}
                            {piiCount > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 font-semibold"><ShieldAlert className="w-3 h-3" /> {piiCount} PII flag{piiCount === 1 ? "" : "s"}</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                    <PartyChip
                        label="Parent"
                        online={presence.parent?.online}
                        lastSeen={presence.parent?.last_seen_at}
                        phone={assignment.parent_phone}
                        name={assignment.student_name}
                        recipientLabel="parent"
                        testid="admin-msg-nudge-parent"
                        senderIsAdmin
                    />
                    <PartyChip
                        label="Tutor"
                        online={presence.tutor?.online}
                        lastSeen={presence.tutor?.last_seen_at}
                        phone={assignment.tutor_phone}
                        name={assignment.tutor_name}
                        recipientLabel="tutor"
                        testid="admin-msg-nudge-tutor"
                        senderIsAdmin
                    />
                </div>
            </div>

            {/* Thread */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div ref={listRef} className="max-h-96 overflow-y-auto overflow-x-auto p-3 bg-slate-50" data-testid="admin-msg-list">
                    {loading ? (
                        <div className="text-center text-xs text-slate-400 py-6"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center text-xs text-slate-400 py-8">No messages yet in this assignment's portal.</div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                                    <th className="pb-1.5">When</th>
                                    <th className="pb-1.5">Sender</th>
                                    <th className="pb-1.5">Message</th>
                                    <th className="pb-1.5 text-center">PII</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((m) => (
                                    <MessageRow key={m.id} message={m} />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Admin composer */}
                <form onSubmit={send} className="border-t border-slate-200 p-2 bg-white flex items-start gap-2">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
                        }}
                        placeholder="Inject a system note visible to both parent & tutor… (Ctrl/⌘+Enter to send)"
                        data-testid="admin-msg-input"
                        rows={2}
                        maxLength={4000}
                        className="flex-1 text-xs border border-slate-200 rounded-lg px-2.5 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none"
                    />
                    <button
                        type="submit"
                        disabled={!draft.trim() || sending}
                        data-testid="admin-msg-send"
                        data-cm-primary
                        className="inline-flex items-center gap-1 bg-cyan-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-cyan-700 disabled:opacity-60 shrink-0"
                    >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Post
                    </button>
                </form>
            </div>
        </div>
    );
}

function PartyChip({ label, online, lastSeen, phone, name, recipientLabel, testid }) {
    const digits = (phone || "").replace(/\D/g, "");
    const hasWa = digits.length >= 8;
    const nudge = `Hi ${name || recipientLabel}, you have a new update from Anand Tutorials. Please open your secure portal.`;
    const waLink = hasWa ? `https://wa.me/${digits}?text=${encodeURIComponent(nudge)}` : null;

    return (
        <div className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full pl-1.5 pr-1 py-0.5">
            <span
                className={`w-2 h-2 rounded-full ${online ? "bg-emerald-500" : "bg-slate-300"}`}
                title={online ? "Online now" : (lastSeen ? `Last seen ${new Date(lastSeen).toLocaleString()}` : "Never seen")}
                data-testid={`admin-msg-presence-dot-${recipientLabel}`}
            />
            <span className="text-[11px] text-slate-700 font-semibold">{label}</span>
            <span className={`text-[10px] ${online ? "text-emerald-700" : "text-slate-400"}`}>
                {online ? "online" : "offline"}
            </span>
            {waLink ? (
                <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={testid}
                    data-cm-compact
                    title={`Nudge ${label.toLowerCase()} on WhatsApp`}
                    className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full hover:bg-emerald-700 ml-1"
                >
                    <MessageSquare className="w-3 h-3" /> WA
                </a>
            ) : (
                <span className="text-[10px] text-slate-400 ml-1 pr-1" title="No phone on file">no WA</span>
            )}
        </div>
    );
}

function MessageRow({ message }) {
    const senderLabel = message.sender === "admin" ? "Anand Tutorials"
        : message.sender === "parent" ? "Parent"
        : message.sender === "tutor" ? "Tutor" : message.sender;

    const attachments = message.attachments || [];

    return (
        <tr className={message.contains_pii ? "bg-amber-50/40" : ""}>
            <td className="py-1.5 pr-2 text-slate-500 whitespace-nowrap align-top">{shortTs(message.created_at)}</td>
            <td className="py-1.5 pr-2 align-top">
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${
                    message.sender === "admin" ? "text-cyan-700"
                        : message.sender === "parent" ? "text-slate-700"
                        : "text-indigo-700"
                }`}>
                    {senderLabel}
                </span>
            </td>
            <td className="py-1.5 pr-2 text-slate-800 align-top">
                <div className="whitespace-pre-wrap break-words">{message.body}</div>
                {attachments.map((a) => (
                    <div key={a.id} className="mt-1 inline-flex items-center gap-1 bg-slate-100 rounded px-1.5 py-0.5 text-[10px] text-slate-700">
                        {a.content_type?.startsWith("image/") ? <ImageIcon className="w-3 h-3" /> :
                         a.content_type === "application/pdf" ? <FileText className="w-3 h-3" /> :
                         <Paperclip className="w-3 h-3" />}
                        <span className="truncate max-w-[220px]">{a.original_filename}</span>
                        <span className="opacity-60">{fmtSize(a.size)}</span>
                    </div>
                ))}
            </td>
            <td className="py-1.5 text-center align-top">
                {message.contains_pii ? (
                    <span
                        title="Sender attempted to share contact details — auto-redacted"
                        data-testid={`admin-msg-pii-flag-${message.id}`}
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-800"
                    >
                        <ShieldAlert className="w-3 h-3" />
                    </span>
                ) : (
                    <span className="text-slate-300 text-[10px]">—</span>
                )}
            </td>
        </tr>
    );
}

function shortTs(iso) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        return sameDay
            ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
}

function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `· ${bytes} B`;
    if (bytes < 1024 * 1024) return `· ${(bytes / 1024).toFixed(0)} KB`;
    return `· ${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
